import { z } from 'zod';

/**
 * Shadow Court rules that both sides need: how many players, which
 * roles are dealt, how big each mission team is, and who knows what.
 *
 * Good (Loyalists of the Crown) must complete three missions; evil (Minions
 * of Warlord) must fail three, or get five teams in a row rejected, or
 * identify Seer at the end. Only the player counts below are legal.
 */

export const COURT_MIN_PLAYERS = 5;
export const COURT_MAX_PLAYERS = 10;
export const COURT_QUESTS = 5;
/** Missions either side needs to win. */
export const COURT_QUESTS_TO_WIN = 3;
/** Teams rejected in a row that hand evil the game. */
export const COURT_MAX_REJECTIONS = 5;

export const COURT_ROLES = ['seer', 'guardian', 'loyalist', 'hunter', 'mimic', 'warlord', 'loner'] as const;
export type CourtRole = (typeof COURT_ROLES)[number];
export const courtRoleSchema = z.enum(COURT_ROLES);

export const COURT_ROLE_INFO: Readonly<Record<CourtRole, { label: string; evil: boolean; description: string }>> = Object.freeze({
  seer: {
    label: 'Seer',
    evil: false,
    description: 'You know who is evil, except Warlord. Guide your side without giving yourself away: if evil finds you, they win.',
  },
  guardian: {
    label: 'Guardian',
    evil: false,
    description: 'You see Seer and Mimic but not which is which. Protect the real Seer.',
  },
  loyalist: {
    label: 'Loyalist',
    evil: false,
    description: 'You know nothing but that you are good. Watch the votes and trust carefully.',
  },
  hunter: {
    label: 'Hunter',
    evil: true,
    description: 'You are evil. If good completes three missions, you get one guess to name Seer and steal the win.',
  },
  mimic: {
    label: 'Mimic',
    evil: true,
    description: 'You are evil, and you look like Seer to Guardian. Pretend to be Seer.',
  },
  warlord: {
    label: 'Warlord',
    evil: true,
    description: 'You are evil, and Seer cannot see you.',
  },
  loner: {
    label: 'Loner',
    evil: true,
    description: 'You are evil, but you do not know the other evil players and they do not know you.',
  },
});

export interface CourtSetup {
  players: number;
  good: number;
  evil: number;
  /** Every role dealt, good first. */
  roles: CourtRole[];
}

const EVIL_COUNT: Readonly<Record<number, number>> = { 5: 2, 6: 2, 7: 3, 8: 3, 9: 3, 10: 4 };

/** The roles dealt for a given player count, or null when the count is not playable. */
export function courtSetup(players: number): CourtSetup | null {
  const evil = EVIL_COUNT[players];
  if (evil === undefined) return null;
  const good = players - evil;
  const goodRoles: CourtRole[] = ['seer', 'guardian'];
  while (goodRoles.length < good) goodRoles.push('loyalist');
  const evilRoles: CourtRole[] = (['hunter', 'mimic', 'warlord', 'loner'] as const).slice(0, evil);
  return { players, good, evil, roles: [...goodRoles, ...evilRoles] };
}

/** Mission team sizes by player count. */
export const COURT_TEAM_SIZES: Readonly<Record<number, readonly number[]>> = Object.freeze({
  5: [2, 3, 2, 3, 3],
  6: [2, 3, 4, 3, 4],
  7: [2, 3, 3, 4, 4],
  8: [3, 4, 4, 5, 5],
  9: [3, 4, 4, 5, 5],
  10: [3, 4, 4, 5, 5],
});

/** With seven or more players the fourth mission needs two fails to fail. */
export function courtNeedsTwoFails(players: number, mission: number): boolean {
  return players >= 7 && mission === 3;
}

/** What one player is shown about another at the start (their alignment, not their exact role). */
export type CourtSight = 'evil' | 'seer' | 'seer_or_mimic';

/**
 * Who a player knows about from their role alone (`roles` is indexed by
 * player). Seer sees evil except Warlord; Guardian sees Seer and Mimic
 * without telling them apart; evil see each other except Loner.
 */
export function courtSights(roles: readonly CourtRole[], viewer: number): Array<{ player: number; as: CourtSight }> {
  const mine = roles[viewer];
  const out: Array<{ player: number; as: CourtSight }> = [];
  roles.forEach((role, player) => {
    if (player === viewer || !mine) return;
    const evil = COURT_ROLE_INFO[role].evil;
    if (mine === 'seer' && evil && role !== 'warlord') out.push({ player, as: 'evil' });
    else if (mine === 'guardian' && (role === 'seer' || role === 'mimic')) out.push({ player, as: 'seer_or_mimic' });
    else if (COURT_ROLE_INFO[mine].evil && mine !== 'loner' && evil && role !== 'loner') out.push({ player, as: 'evil' });
  });
  return out;
}
